import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate, NavLink } from 'react-router-dom';
import queryString from 'query-string';
import {
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  ButtonPseudo,
  ButtonSecondary,
  IconDownload,
  ButtonSize,
  IconRight,
  IconLoader,
  IconLeft,
  PermissionEnum,
  formatBalance,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  useLazyGetOrganizationWarningReportByIdQuery,
  useExportWarningReportMutation,
  useLazyGetOrganizationMonitoringRulesWarningsReportQuery,
} from './store/service';
import {
  setOrganizationsWarningDetails,
  clearOrganizationsWarningDetailsState,
} from './store/slice';
import WarningItemRows from '../../components/warnings-report/warning-item-rows';
import WarningItemRow from '../../components/warnings-report/warning-item-row';

import cn from './warning-report.module.css';

import ROUTES from '../../routes';

const CustomerWarningDetails = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [maxCount, setMaxCount] = useState(0);
  const [totalWarnings, setTotalWarnings] = useState(0);
  const [organization, setOrganization] = useState<
    { id: string; type: string; name: string } | undefined
  >(undefined);
  const [showDetalisation, setShowDetalisation] = useState(false);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [maxMonitoringRulesCount, setMaxMonitoringRulseCount] = useState(0);

  const {
    warningsReport: {
      organizationWarningDetails,
      organizationWarnings,
      organizationMonitoringRulesWarningsReports,
    },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { organizationId } = useParams();

  const [getOrganizationWarningReportById, { isLoading, isFetching }] =
    useLazyGetOrganizationWarningReportByIdQuery();
  const [
    exportWarningReport,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportWarningReportMutation();

  const [
    getOrganizationMonitoringRulesWarningsReport,
    {
      isLoading: organizationMonitoringRulesIsLoading,
      isFetching: organizationMonitoringRulesIsFetching,
    },
  ] = useLazyGetOrganizationMonitoringRulesWarningsReportQuery();

  const navigate = useNavigate();
  const { search } = useLocation();

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string> = {
      currentOrganizationId: organizationId as string,
    };

    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.createdAtFrom) {
        exportFilters.createdAtFrom = parsed.createdAtFrom as string;

        if (parsed.createdAtTo) {
          exportFilters.createdAtTo = parsed.createdAtTo as string;
        }
      }

      if (parsed.organizationType) {
        exportFilters.organizationType = parsed.organizationType as string[];
      }

      if (parsed.transactionTypeId) {
        exportFilters.transactionTypeId = parsed.transactionTypeId as string[];
      }

      if (parsed.transactionPaymentType) {
        exportFilters.transactionPaymentType =
          parsed.transactionPaymentType as string[];
      }

      if (parsed.paymentOrderType) {
        exportFilters.paymentOrderType = parsed.paymentOrderType as string[];
      }
    }

    await exportWarningReport(exportFilters);
  };

  const onShowMoreClick = async () => {
    setNextPageIsLoading(true);

    const response = await getOrganizationMonitoringRulesWarningsReport({
      id: organizationId as string,
      query: `&take=20&cursor=${
        organizationMonitoringRulesWarningsReports?.monitoringsRulesTotals
          .pageInfo.endCursor
      }${
        search
          ? search
              ?.split('&')
              ?.filter((item) => !item.includes('selectedTypeId'))
              ?.join('&')
              ?.replace('?', '&')
          : ''
      }`,
    });

    if (response) {
      setNextPageIsLoading(false);
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setMaxCount(0);
    setTotalWarnings(0);
    dispatch(clearOrganizationsWarningDetailsState());
    setOrganization(undefined);

    setTimeout(() => {
      navigate(`${ROUTES.WARNING_REPORT_CUSTOMERS}${search || ''}`);
    }, 100);
  };

  const getOrganizationWarningReportByIdAction = useCallback(
    async ({ id, query }: { id: string; query?: string }) => {
      const response = await getOrganizationWarningReportById({
        id,
        query: query ? query?.replace('?', '&') : '',
      });

      if (response?.data?.data) {
        dispatch(setOrganizationsWarningDetails(response?.data?.data));

        setTotalWarnings(response?.data?.data?.totals?.total);

        const counts = Object.keys(response?.data?.data?.totals)?.map(
          // @ts-ignore
          (item) => response?.data?.data?.totals[item],
        );

        setMaxCount(Math.max(...counts));
      }
    },
    [],
  );

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (organizationId && !showDetalisation) {
      getOrganizationWarningReportByIdAction({
        id: organizationId,
        query: search
          ?.split('&')
          ?.filter((item) => !item.includes('selectedTypeId'))
          ?.join('&'),
      });

      if (organizationWarnings) {
        const currentWarning = organizationWarnings?.organizations?.find(
          (item) => item.node.id === organizationId,
        );

        if (currentWarning) {
          setOrganization({
            id: currentWarning?.node?.id,
            type: currentWarning?.node?.type,
            name: currentWarning?.node?.name,
          });
        }
      }
    }
  }, [organizationId, organizationWarnings, search, showDetalisation]);

  useEffect(() => {
    if (showDetalisation && organizationId) {
      getOrganizationMonitoringRulesWarningsReport({
        id: organizationId,
        query: `&take=20${
          search
            ? search
                ?.split('&')
                ?.filter((item) => !item.includes('selectedTypeId'))
                ?.join('&')
                ?.replace('?', '&')
            : ''
        }`,
      });
    }
  }, [showDetalisation, organizationId, search]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'warnings-report',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (showDetalisation && organizationMonitoringRulesWarningsReports) {
      const counts =
        organizationMonitoringRulesWarningsReports?.monitoringsRulesTotals?.monitoringsRules?.map(
          (item) => item.warningsTotal,
        );

      setMaxMonitoringRulseCount(Math.max(...counts));
    }
  }, [showDetalisation, organizationMonitoringRulesWarningsReports]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching && organizationWarningDetails) {
    let organizationNameContent;

    organizationNameContent = organization?.name;

    if (
      (organization?.type === 'INDIVIDUAL' ||
        organization?.type === 'COMPANY_OFFICIAL') &&
      activePermissions?.includes(PermissionEnum.INDIVIDUAL_CUSTOMERS_LIST)
    ) {
      organizationNameContent = (
        <NavLink
          to={`/customers/individual/${organization?.id}`}
          target="_blank"
          className={cn.organizationLink}
        >
          {organization?.name}
          <IconRight />
        </NavLink>
      );
    } else if (
      organization?.type === 'CORPORATE' &&
      activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_LIST)
    ) {
      organizationNameContent = (
        <NavLink
          to={`/customers/corporate/${organization?.id}`}
          target="_blank"
          className={cn.organizationLink}
        >
          {organization?.name}
          <IconRight />
        </NavLink>
      );
    }

    content = (
      <>
        <div className={cn.detailsTitle}>
          {organizationNameContent}
          {activePermissions?.includes(
            PermissionEnum.WARNINGS_DOWNLOAD_ACTION,
          ) &&
            !showDetalisation && (
              <ButtonSecondary
                text={exportIsLoading ? 'Loading...' : 'Download Report'}
                icon={<IconDownload />}
                className={`button-white ${cn.downloadButton}`}
                size={ButtonSize.SMALL}
                onClick={onExportClick}
              />
            )}
        </div>
        <div className={cn.warningDetailsSeparator} />
        {showDetalisation && (
          <div className={cn.organizationMonitoringRulesTitle}>
            <button
              type="button"
              className={cn.hideDetalisation}
              onClick={() => {
                setShowDetalisation(false);
              }}
            >
              <IconLeft />
            </button>
            <div className={cn.detalisationTitle}>
              Transactions monitoring rules
            </div>
          </div>
        )}
        <div className={cn.warningDetailsTotalWrapper}>
          <div className={cn.warningDetailsTotal}>
            <div className={cn.warningDetailsTotalValue}>
              {showDetalisation
                ? formatBalance(
                    (organizationMonitoringRulesWarningsReports?.total as number) ||
                      0,
                    undefined,
                    0,
                  )
                : formatBalance(totalWarnings || 0, undefined, 0)}
            </div>
            <div className={cn.warningDetailsTotalText}>Total warnings</div>
            {search
              ?.split('&')
              ?.filter(
                (item) =>
                  !item.includes('selectedTypeId') && !item.includes('cursor'),
              )
              ?.join('&')?.length > 0 && (
              <>
                <div className={cn.warningDetailsTotalSeprator}> / </div>
                <div className={cn.warningDetailsTotalValue}>
                  {showDetalisation
                    ? formatBalance(
                        (organizationMonitoringRulesWarningsReports?.filteredTotal as number) ||
                          0,
                        undefined,
                        0,
                      )
                    : formatBalance(
                        organizationWarningDetails?.filteredTotals?.total || 0,
                        undefined,
                        0,
                      )}
                </div>
                <div className={cn.warningDetailsTotalText}>Selected</div>
              </>
            )}
          </div>
          <NavLink
            to={`${
              ROUTES.WARNING_REPORT_LIST
            }?organizationId=${organizationId}${
              search
                ? search
                    ?.split('&')
                    ?.filter(
                      (item) =>
                        !item.includes('selectedTypeId') &&
                        !item.includes('cursor'),
                    )
                    ?.join('&')
                    .replace('?', '&')
                : ''
            }${showDetalisation ? '&type=MONITORING' : ''}`}
            className={cn.listLink}
            target="_blank"
          >
            Show in list <IconRight />
          </NavLink>
        </div>
        {!showDetalisation && (
          <WarningItemRows
            maxCount={maxCount}
            warningsNumbersOfAlerts={{
              totals: organizationWarningDetails?.totals,
              filteredTotals: organizationWarningDetails?.filteredTotals,
            }}
            filtersLength={
              search
                ?.split('&')
                ?.filter(
                  (item) =>
                    !item.includes('selectedTypeId') &&
                    !item.includes('cursor'),
                )
                ?.join('&')?.length
            }
            setShowDetalisation={setShowDetalisation}
            organizationDetails
          />
        )}
        {showDetalisation && (
          <>
            {(organizationMonitoringRulesIsLoading ||
              organizationMonitoringRulesIsFetching) &&
              !nextPageIsLoading && (
                <div
                  className={`${cn.monitoringRulesWarningsLoader} ${cn.organizationMonitoringRulesWarningsLoader}`}
                >
                  <IconLoader />
                </div>
              )}

            {organizationMonitoringRulesWarningsReports &&
              organizationMonitoringRulesWarningsReports?.monitoringsRulesTotals?.monitoringsRules?.map(
                (item) => (
                  <WarningItemRow
                    key={item.node.id}
                    title={item.node.name}
                    warningItem={item.warningsTotal}
                    maxCount={maxMonitoringRulesCount}
                    filteredWarningItem={
                      search
                        ?.split('&')
                        ?.filter(
                          (filterItem) =>
                            !filterItem.includes('selectedTypeId') &&
                            !filterItem.includes('cursor'),
                        )
                        ?.join('&')?.length > 0
                        ? item.filteredWarningsTotal
                        : undefined
                    }
                    organizationDetails
                    monitoringRules
                  />
                ),
              )}
            {organizationMonitoringRulesWarningsReports?.monitoringsRulesTotals
              ?.pageInfo?.hasNextPage && (
              <div className={cn.showMoreWrapper}>
                <ButtonPseudo
                  size={ButtonSize.SMALL}
                  text={nextPageIsLoading ? 'Loading...' : 'Show more'}
                  onClick={onShowMoreClick}
                />
              </div>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <SidePanel
      isOpen={panelIsOpen}
      closeOnClickOutside
      onClose={handleClose}
      title="Detailing of warnings by Customer"
      sidepanelExpandedKey="warning-by-customer-details-panel"
      type={SidePanelType.Transactions}
      footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
    >
      {content}
    </SidePanel>
  );
};

export default CustomerWarningDetails;
